import React, { useState, useEffect } from 'react';
import { 
  FaUserTie, FaPlus, FaEdit, FaTrash, FaTasks, FaClock, 
  FaWallet, FaChartLine, FaTimes, FaCheckCircle, FaPhone,
  FaMapMarkerAlt, FaSearch, FaFileInvoice, FaCalendarAlt, FaStar
} from 'react-icons/fa';
import './VendorManagement.css';
import * as vendorApi from '../../api/vendorApi';

const VendorManagement = () => {
  const [activeTab, setActiveTab] = useState('vendors');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  
  // Modal States
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalType, setModalType] = useState('');
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState({
    total_vendors: 0,
    active_vendors: 0,
    active_jobs: 0,
    pending_payments: 0
  });

  // Data States
  const [vendors, setVendors] = useState([]);
  const [workLogs, setWorkLogs] = useState([]);
  const [payments, setPayments] = useState([]);

  // Form States
  const [vendorForm, setVendorForm] = useState({
    name: '',
    category: 'Landscaping',
    contact: '',
    email: '',
    address: '',
    status: 'Active'
  });

  const [jobForm, setJobForm] = useState({
    vendorId: '',
    jobType: '',
    description: '',
    startDate: '',
    estimatedHours: ''
  });

  const [paymentForm, setPaymentForm] = useState({
    vendorId: '',
    amount: '',
    paymentMethod: 'Bkash',
    notes: '',
    paymentDate: new Date().toISOString().split('T')[0]
  });

  // Fetch data on component mount
  useEffect(() => {
    fetchVendors();
    fetchStats();
  }, []);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchVendors();
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm, filterStatus]);

  useEffect(() => {
    if (activeTab === 'workLogs') {
      fetchWorkLogs();
    } else if (activeTab === 'payments') {
      fetchPayments();
    }
  }, [activeTab]);

  const fetchVendors = async () => {
    try {
      setLoading(true);
      const response = await vendorApi.getVendors({
        search: searchTerm,
        status: filterStatus
      });
      setVendors(response.data || []);
    } catch (error) {
      console.error('Error fetching vendors:', error);
      showErrorToast(error.response?.data?.message || 'Failed to fetch vendors');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await vendorApi.getVendorStats();
      setStats(response.data || stats);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchWorkLogs = async () => {
    try {
      setLoading(true);
      const response = await vendorApi.getWorkLogs();
      const logs = (response.data || []).map(log => ({
        id: log.id,
        vendorId: log.vendor_id,
        vendorName: log.vendor?.name || '',
        date: log.start_date,
        jobType: log.job_type,
        hoursWorked: log.hours_worked || log.estimated_hours,
        status: log.status
      }));
      setWorkLogs(logs);
    } catch (error) {
      console.error('Error fetching work logs:', error);
      showErrorToast(error.response?.data?.message || 'Failed to fetch work logs');
    } finally {
      setLoading(false);
    }
  };

  const fetchPayments = async () => {
    try {
      setLoading(true);
      const response = await vendorApi.getPayments();
      const paymentData = (response.data || []).map(payment => ({
        id: payment.id,
        vendorId: payment.vendor_id,
        vendorName: payment.vendor?.name || '',
        amount: parseFloat(payment.amount),
        date: payment.payment_date,
        status: payment.status,
        invoiceNo: payment.invoice_number
      }));
      setPayments(paymentData);
    } catch (error) {
      console.error('Error fetching payments:', error);
      showErrorToast(error.response?.data?.message || 'Failed to fetch payments');
    } finally {
      setLoading(false);
    }
  };

  const showErrorToast = (message) => {
    setToastMessage(message);
    setShowToast(true);
    setTimeout(() => setShowToast(false), 3000);
  };

  // Handlers
  const openModal = (type, vendor = null) => {
    setModalType(type);
    setSelectedVendor(vendor);
    if (vendor && type === 'edit') {
      setVendorForm({
        name: vendor.name,
        category: vendor.category,
        contact: vendor.contact,
        email: vendor.email,
        address: vendor.address,
        status: vendor.status
      });
    }
    if (type === 'assign' && vendor) setJobForm({ ...jobForm, vendorId: vendor.id });
    if (type === 'payment' && vendor) setPaymentForm({ 
      ...paymentForm, 
      vendorId: vendor.id, 
      amount: vendor.pendingPayment || vendor.pending_payment || 0,
      paymentDate: new Date().toISOString().split('T')[0]
    });
    
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setModalType('');
    setSelectedVendor(null);
    setVendorForm({ name: '', category: 'Landscaping', contact: '', email: '', address: '', status: 'Active' });
    setJobForm({ vendorId: '', jobType: '', description: '', startDate: '', estimatedHours: '' });
    setPaymentForm({ vendorId: '', amount: '', paymentMethod: 'Bkash', notes: '' });
  };

  const showSuccessToast = (message) => {
    setToastMessage(message);
    setShowToast(true);
    setTimeout(() => setShowToast(false), 3000);
  };

  const handleAddVendor = async () => {
    try {
      setLoading(true);
      await vendorApi.createVendor(vendorForm);
      showSuccessToast('Vendor added successfully!');
      closeModal();
      fetchVendors();
      fetchStats();
    } catch (error) {
      console.error('Error adding vendor:', error);
      showErrorToast(error.response?.data?.message || 'Failed to add vendor');
    } finally {
      setLoading(false);
    }
  };

  const handleEditVendor = async () => {
    try {
      setLoading(true);
      await vendorApi.updateVendor(selectedVendor.id, vendorForm);
      showSuccessToast('Vendor updated successfully!');
      closeModal();
      fetchVendors();
    } catch (error) {
      console.error('Error updating vendor:', error);
      showErrorToast(error.response?.data?.message || 'Failed to update vendor');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteVendor = async () => {
    try {
      setLoading(true);
      await vendorApi.deleteVendor(selectedVendor.id);
      showSuccessToast('Vendor removed successfully!');
      closeModal();
      fetchVendors();
      fetchStats();
    } catch (error) {
      console.error('Error deleting vendor:', error);
      showErrorToast(error.response?.data?.message || 'Failed to delete vendor');
    } finally {
      setLoading(false);
    }
  };

  const handleAssignJob = async () => {
    try {
      setLoading(true);
      const jobData = {
        vendor_id: parseInt(jobForm.vendorId),
        job_type: jobForm.jobType,
        description: jobForm.description,
        start_date: jobForm.startDate,
        estimated_hours: parseInt(jobForm.estimatedHours)
      };
      await vendorApi.assignJob(jobData);
      showSuccessToast('Job assigned successfully!');
      closeModal();
      fetchVendors();
      fetchWorkLogs();
      fetchStats();
    } catch (error) {
      console.error('Error assigning job:', error);
      showErrorToast(error.response?.data?.message || 'Failed to assign job');
    } finally {
      setLoading(false);
    }
  };

  const handleProcessPayment = async () => {
    try {
      setLoading(true);
      const paymentData = {
        vendor_id: parseInt(paymentForm.vendorId),
        amount: parseFloat(paymentForm.amount),
        payment_method: paymentForm.paymentMethod,
        payment_date: paymentForm.paymentDate || new Date().toISOString().split('T')[0],
        notes: paymentForm.notes
      };
      await vendorApi.processPayment(paymentData);
      showSuccessToast('Payment processed successfully!');
      closeModal();
      fetchVendors();
      fetchPayments();
      fetchStats();
    } catch (error) {
      console.error('Error processing payment:', error);
      showErrorToast(error.response?.data?.message || 'Failed to process payment');
    } finally {
      setLoading(false);
    }
  };

  // Map vendor data to match frontend expectations
  const filteredVendors = vendors.map(vendor => ({
    ...vendor,
    totalJobs: vendor.total_jobs || 0,
    pendingPayment: vendor.pending_payment || 0
  }));

  return (
    <div className="vm-wrapper">
      <div className="vm-container">
        
        {/* Header */}
        <header className="vm-header">
          <div>
            <h1 className="vm-title">Vendor Management</h1>
            <p className="vm-subtitle">Manage external vendors and track their performance</p>
          </div>
          {showToast && (
            <div className="vm-toast">
              <FaCheckCircle /> {toastMessage}
            </div>
          )}
        </header>

        {/* Stats Cards */}
        <div className="vm-stats-grid">
          <div className="vm-stat-card">
            <div className="vm-stat-icon icon-blue"><FaUserTie /></div>
            <div>
              <div className="vm-stat-value">{stats.total_vendors}</div>
              <div className="vm-stat-label">Total Vendors</div>
            </div>
          </div>
          <div className="vm-stat-card">
            <div className="vm-stat-icon icon-green"><FaTasks /></div>
            <div>
              <div className="vm-stat-value">{stats.active_jobs}</div>
              <div className="vm-stat-label">Active Jobs</div>
            </div>
          </div>
          <div className="vm-stat-card">
            <div className="vm-stat-icon icon-orange"><FaWallet /></div>
            <div>
              <div className="vm-stat-value">৳{stats.pending_payments.toLocaleString()}</div>
              <div className="vm-stat-label">Pending Payments</div>
            </div>
          </div>
          <div className="vm-stat-card">
            <div className="vm-stat-icon icon-red"><FaChartLine /></div>
            <div>
              <div className="vm-stat-value">{stats.active_vendors}</div>
              <div className="vm-stat-label">Active Vendors</div>
            </div>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="vm-tab-container">
          <button 
            className={`vm-tab ${activeTab === 'vendors' ? 'active' : ''}`}
            onClick={() => setActiveTab('vendors')}
          >
            <FaUserTie /> Vendor List
          </button>
          <button 
            className={`vm-tab ${activeTab === 'workLogs' ? 'active' : ''}`}
            onClick={() => setActiveTab('workLogs')}
          >
            <FaClock /> Work Logs
          </button>
          <button 
            className={`vm-tab ${activeTab === 'payments' ? 'active' : ''}`}
            onClick={() => setActiveTab('payments')}
          >
            <FaWallet /> Payments
          </button>
          <button 
            className={`vm-tab ${activeTab === 'reports' ? 'active' : ''}`}
            onClick={() => setActiveTab('reports')}
          >
            <FaChartLine /> Reports
          </button>
        </div>

        {/* Main Content */}
        <div className="vm-main-card">
          
          {/* VENDORS TAB */}
          {activeTab === 'vendors' && (
            <div>
              <div className="vm-toolbar">
                <div className="vm-search-wrapper">
                  <FaSearch className="vm-search-icon" />
                  <input 
                    type="text"
                    placeholder="Search vendors..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="vm-search-input"
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        fetchVendors();
                      }
                    }}
                  />
                </div>
                <div className="vm-toolbar-actions">
                  <select 
                    value={filterStatus}
                    onChange={(e) => {
                      setFilterStatus(e.target.value);
                      fetchVendors();
                    }}
                    className="vm-filter-select"
                  >
                    <option value="all">All Status</option>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                  <button className="vm-add-btn" onClick={() => openModal('add')}>
                    <FaPlus /> Add Vendor
                  </button>
                </div>
              </div>

              <div className="vm-table-wrapper">
                <table className="vm-table">
                  <thead>
                    <tr>
                      <th>Vendor Name</th>
                      <th>Category</th>
                      <th>Contact</th>
                      <th>Location</th>
                      <th>Rating</th>
                      <th>Jobs</th>
                      <th>Pending</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredVendors.map(vendor => (
                      <tr key={vendor.id}>
                        <td>
                          <div className="vm-vendor-name">
                            <div className="vm-vendor-avatar">{vendor.name.charAt(0)}</div>
                            <div>
                              <strong>{vendor.name}</strong>
                              <div className="vm-vendor-email">{vendor.email}</div>
                            </div>
                          </div>
                        </td>
                        <td>{vendor.category}</td>
                        <td>
                          <div className="vm-contact-info">
                            <FaPhone className="vm-icon-small" />
                            {vendor.contact}
                          </div>
                        </td>
                        <td>
                          <div className="vm-contact-info">
                            <FaMapMarkerAlt className="vm-icon-small" />
                            {vendor.address}
                          </div>
                        </td>
                        <td>
                          <div className="vm-rating">
                            <FaStar className="vm-star-icon" /> {vendor.rating}
                          </div>
                        </td>
                        <td>{vendor.totalJobs || vendor.total_jobs || 0}</td>
                        <td>
                          <span className={(vendor.pendingPayment || vendor.pending_payment || 0) > 0 ? 'vm-pending-amount' : 'vm-paid-amount'}>
                            ৳{(vendor.pendingPayment || vendor.pending_payment || 0).toLocaleString()}
                          </span>
                        </td>
                        <td>
                          <span className={vendor.status === 'Active' ? 'vm-status-active' : 'vm-status-inactive'}>
                            {vendor.status}
                          </span>
                        </td>
                        <td>
                          <div className="vm-action-btns">
                            <button className="vm-icon-btn" onClick={() => openModal('assign', vendor)} title="Assign Job"><FaTasks /></button>
                            <button className="vm-icon-btn" onClick={() => openModal('payment', vendor)} title="Process Payment"><FaWallet /></button>
                            <button className="vm-icon-btn" onClick={() => openModal('edit', vendor)} title="Edit"><FaEdit /></button>
                            <button className="vm-icon-btn delete" onClick={() => openModal('delete', vendor)} title="Delete"><FaTrash /></button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* WORK LOGS TAB */}
          {activeTab === 'workLogs' && (
            <div>
              <div className="vm-section-header">
                <h3 className="vm-section-title">Attendance & Work Logs</h3>
              </div>
              <div className="vm-table-wrapper">
                {loading && <div style={{ padding: '20px', textAlign: 'center' }}>Loading...</div>}
                <table className="vm-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Vendor</th>
                      <th>Job Type</th>
                      <th>Hours Worked</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {!loading && workLogs.length === 0 && (
                      <tr>
                        <td colSpan="5" style={{ textAlign: 'center', padding: '20px' }}>
                          No work logs found
                        </td>
                      </tr>
                    )}
                    {workLogs.map(log => (
                      <tr key={log.id}>
                        <td>
                          <div className="vm-date-cell">
                            <FaCalendarAlt className="vm-icon-small" />
                            {log.date || log.start_date}
                          </div>
                        </td>
                        <td>{log.vendorName || log.vendor?.name}</td>
                        <td>{log.jobType || log.job_type}</td>
                        <td>{(log.hoursWorked || log.hours_worked || log.estimated_hours || 0)} hrs</td>
                        <td>
                          <span className={log.status === 'Completed' ? 'vm-status-completed' : 'vm-status-progress'}>
                            {log.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* PAYMENTS TAB */}
          {activeTab === 'payments' && (
            <div>
              <div className="vm-section-header">
                <h3 className="vm-section-title">Payment Records</h3>
              </div>
              <div className="vm-table-wrapper">
                {loading && <div style={{ padding: '20px', textAlign: 'center' }}>Loading...</div>}
                <table className="vm-table">
                  <thead>
                    <tr>
                      <th>Invoice #</th>
                      <th>Vendor</th>
                      <th>Amount</th>
                      <th>Date</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {!loading && payments.length === 0 && (
                      <tr>
                        <td colSpan="6" style={{ textAlign: 'center', padding: '20px' }}>
                          No payments found
                        </td>
                      </tr>
                    )}
                    {payments.map(payment => (
                      <tr key={payment.id}>
                        <td>
                          <div className="vm-invoice-cell">
                            <FaFileInvoice className="vm-icon-primary" />
                            {payment.invoiceNo || payment.invoice_number}
                          </div>
                        </td>
                        <td>{payment.vendorName || payment.vendor?.name}</td>
                        <td><strong>৳{payment.amount.toLocaleString()}</strong></td>
                        <td>{payment.date || payment.payment_date}</td>
                        <td>
                          <span className={payment.status === 'Paid' ? 'vm-status-paid' : 'vm-status-pending'}>
                            {payment.status}
                          </span>
                        </td>
                        <td>
                          <button className="vm-view-btn">View Invoice</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* REPORTS TAB */}
          {activeTab === 'reports' && (
            <div className="vm-reports-container">
              <div className="vm-report-card">
                <FaChartLine className="vm-report-icon" />
                <h3>Vendor Performance Report</h3>
                <p>Analyze vendor ratings, completion rates, and efficiency metrics</p>
                <button className="vm-generate-btn">Generate Report</button>
              </div>
              <div className="vm-report-card">
                <FaWallet className="vm-report-icon" />
                <h3>Payment Summary</h3>
                <p>View total expenditure, pending payments, and transaction history</p>
                <button className="vm-generate-btn">Generate Report</button>
              </div>
              <div className="vm-report-card">
                <FaTasks className="vm-report-icon" />
                <h3>Job Completion Analysis</h3>
                <p>Track completed vs pending jobs, time efficiency, and workload distribution</p>
                <button className="vm-generate-btn">Generate Report</button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* MODALS */}
      {isModalOpen && (
        <div className="vm-modal-overlay" onClick={closeModal}>
          <div className="modal-content large" onClick={(e) => e.stopPropagation()}>
            
            {/* ADD/EDIT VENDOR */}
            {(modalType === 'add' || modalType === 'edit') && (
              <>
                <div className="vm-modal-header">
                  <h3>{modalType === 'add' ? 'Add New Vendor' : 'Edit Vendor'}</h3>
                  <button className="vm-close-btn" onClick={closeModal}><FaTimes /></button>
                </div>
                <div className="vm-modal-body">
                  <div className="vm-form-grid">
                    <div className="vm-input-group">
                      <label>Vendor Name</label>
                      <input 
                        type="text" 
                        value={vendorForm.name}
                        onChange={(e) => setVendorForm({...vendorForm, name: e.target.value})}
                        placeholder="Enter vendor name"
                      />
                    </div>
                    <div className="vm-input-group">
                      <label>Category</label>
                      <select 
                        value={vendorForm.category}
                        onChange={(e) => setVendorForm({...vendorForm, category: e.target.value})}
                      >
                        <option>Landscaping</option>
                        <option>Construction</option>
                        <option>Security</option>
                        <option>Electrical</option>
                        <option>Plumbing</option>
                      </select>
                    </div>
                    <div className="vm-input-group">
                      <label>Contact Number</label>
                      <input 
                        type="text" 
                        value={vendorForm.contact}
                        onChange={(e) => setVendorForm({...vendorForm, contact: e.target.value})}
                        placeholder="+880 17XX-XXXXXX"
                      />
                    </div>
                    <div className="vm-input-group">
                      <label>Email</label>
                      <input 
                        type="email" 
                        value={vendorForm.email}
                        onChange={(e) => setVendorForm({...vendorForm, email: e.target.value})}
                        placeholder="vendor@example.com"
                      />
                    </div>
                    <div className="vm-input-group full-width">
                      <label>Address</label>
                      <textarea 
                        value={vendorForm.address}
                        onChange={(e) => setVendorForm({...vendorForm, address: e.target.value})}
                        placeholder="Enter complete address"
                      />
                    </div>
                    <div className="vm-input-group">
                      <label>Status</label>
                      <select 
                        value={vendorForm.status}
                        onChange={(e) => setVendorForm({...vendorForm, status: e.target.value})}
                      >
                        <option>Active</option>
                        <option>Inactive</option>
                      </select>
                    </div>
                  </div>
                </div>
                <div className="vm-modal-footer">
                  <button className="vm-btn-secondary" onClick={closeModal}>Cancel</button>
                  <button 
                    className="vm-btn-primary" 
                    onClick={modalType === 'add' ? handleAddVendor : handleEditVendor}
                  >
                    {modalType === 'add' ? 'Add Vendor' : 'Update Vendor'}
                  </button>
                </div>
              </>
            )}

            {/* DELETE CONFIRMATION */}
            {modalType === 'delete' && (
              <>
                <div className="vm-modal-header">
                  <h3>Confirm Deletion</h3>
                  <button className="vm-close-btn" onClick={closeModal}><FaTimes /></button>
                </div>
                <div className="vm-modal-body">
                  <p className="vm-confirm-text">
                    Are you sure you want to remove <strong>{selectedVendor?.name}</strong>? This action cannot be undone.
                  </p>
                </div>
                <div className="vm-modal-footer">
                  <button className="vm-btn-secondary" onClick={closeModal}>Cancel</button>
                  <button className="vm-btn-danger" onClick={handleDeleteVendor}>
                    Delete Vendor
                  </button>
                </div>
              </>
            )}

            {/* ASSIGN JOB */}
            {modalType === 'assign' && (
              <>
                <div className="vm-modal-header">
                  <h3>Assign Maintenance Job</h3>
                  <button className="vm-close-btn" onClick={closeModal}><FaTimes /></button>
                </div>
                <div className="vm-modal-body">
                  <div className="vm-form-grid">
                    <div className="vm-input-group full-width">
                      <label>Vendor</label>
                      <input type="text" value={selectedVendor?.name} disabled />
                    </div>
                    <div className="vm-input-group">
                      <label>Job Type</label>
                      <input 
                        type="text" 
                        value={jobForm.jobType}
                        onChange={(e) => setJobForm({...jobForm, jobType: e.target.value})}
                        placeholder="e.g., Garden Maintenance"
                      />
                    </div>
                    <div className="vm-input-group">
                      <label>Start Date</label>
                      <input 
                        type="date" 
                        value={jobForm.startDate}
                        onChange={(e) => setJobForm({...jobForm, startDate: e.target.value})}
                        required
                      />
                    </div>
                    <div className="vm-input-group">
                      <label>Estimated Hours</label>
                      <input 
                        type="number" 
                        value={jobForm.estimatedHours}
                        onChange={(e) => setJobForm({...jobForm, estimatedHours: e.target.value})}
                        placeholder="Hours"
                      />
                    </div>
                    <div className="vm-input-group full-width">
                      <label>Job Description</label>
                      <textarea 
                        value={jobForm.description}
                        onChange={(e) => setJobForm({...jobForm, description: e.target.value})}
                        placeholder="Describe the maintenance work required..."
                      />
                    </div>
                  </div>
                </div>
                <div className="vm-modal-footer">
                  <button className="vm-btn-secondary" onClick={closeModal}>Cancel</button>
                  <button className="vm-btn-primary" onClick={handleAssignJob}>Assign Job</button>
                </div>
              </>
            )}

            {/* PROCESS PAYMENT */}
            {modalType === 'payment' && (
              <>
                <div className="vm-modal-header">
                  <h3>Process Vendor Payment</h3>
                  <button className="vm-close-btn" onClick={closeModal}><FaTimes /></button>
                </div>
                <div className="vm-modal-body">
                  <div className="vm-form-grid">
                    <div className="vm-input-group full-width">
                      <label>Vendor</label>
                      <input type="text" value={selectedVendor?.name} disabled />
                    </div>
                    <div className="vm-input-group">
                      <label>Payment Amount (৳)</label>
                      <input 
                        type="number" 
                        value={paymentForm.amount}
                        onChange={(e) => setPaymentForm({...paymentForm, amount: e.target.value})}
                        required
                      />
                    </div>
                    <div className="vm-input-group">
                      <label>Payment Date</label>
                      <input 
                        type="date" 
                        value={paymentForm.paymentDate}
                        onChange={(e) => setPaymentForm({...paymentForm, paymentDate: e.target.value})}
                        required
                      />
                    </div>
                    <div className="vm-input-group">
                      <label>Payment Method</label>
                      <select 
                        value={paymentForm.paymentMethod}
                        onChange={(e) => setPaymentForm({...paymentForm, paymentMethod: e.target.value})}
                      >
                        <option>Bkash</option>
                        <option>Nogod</option>
                        <option>Rocket</option>
                        <option>Bank Transfer</option>
                        <option>Cash</option>
                      </select>
                    </div>
                    <div className="vm-input-group full-width">
                      <label>Payment Notes</label>
                      <textarea 
                        value={paymentForm.notes}
                        onChange={(e) => setPaymentForm({...paymentForm, notes: e.target.value})}
                        placeholder="Add any additional notes..."
                      />
                    </div>
                  </div>
                </div>
                <div className="vm-modal-footer">
                  <button className="vm-btn-secondary" onClick={closeModal}>Cancel</button>
                  <button className="vm-btn-primary" onClick={handleProcessPayment}>Process Payment</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default VendorManagement;